import { ItemWorkareaData, ServerState, ViewItem } from './itemModel'
import { Row, Space, Table, Tooltip, Typography } from 'antd'
import { centsToDollarsDisplay, percentageDisplay } from '../displayFormatters'
import { ReactElement, useContext, useRef, useState } from 'react'
import { ColumnType } from 'antd/lib/table'
import { WorkareaItemType, WorkareaItemTypes } from '../../common/constants'
import OverrideNumericValueModal from '../OverrideModals/OverrideNumericValueModal'
import OverrideStringValueModal from '../OverrideModals/OverrideStringValueModal'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { DraggableSortableRow, UndraggableSortableRow, DraggableSortableHeader, UndraggableSortableHeader } from './SortableRow'
import { YearlyBudget } from '../../domain/budget'
import styled from 'styled-components'
import CustomizableValue from './CustomizableValue'
import { ItemsContext } from './ItemsRoot'
import LockableSwitch from './LockableSwitch'
import VisitDetail from './VisitDetail'
import InvoicedIcon from './InvoicedIcon'
import ItemQuantity from './ItemQuantity'
import { styleDeletedRows } from './styleDeletedRows'
import ItemDiffersIcon from './ItemDiffersIcon'
import { CatalogItemDndContext } from '../ItemCatalog/CatalogBrowserDndContext'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlassPlus, faRotate } from '@fortawesome/pro-solid-svg-icons'
import ItemDeleteLink from './ItemDeleteLink'

const { Link, Text } = Typography
const StyledTable = styled(styleDeletedRows<typeof Table<ViewItem>>(Table))`
  min-width: 900px;
  width: 100%;
  td.item-name {
    width: 100px;

    @media (width > 1700px) {
      width: 200px;
    }

    @media (width > 1900px) {
      width: 300px;
    }

    .detail-icon {
      visibility: hidden;
    }

    &:hover .detail-icon {
      visibility: visible;
    }
  }

  td.workarea-name {
    width: 100px;

    @media (width > 1700px) {
      width: 200px;
    }

    @media (width > 1900px) {
      width: 300px;
    }
  }
`

export type Props = {
  viewItems: ViewItem[]
  workareaItemType: WorkareaItemType
  budgets: YearlyBudget[]
  serverState: ServerState
  isLoading: boolean
  visibleColumns: ItemTableColumn[]
  workareas: ItemWorkareaData[]
  sortable?: boolean
  customSummary?: (items: ViewItem[]) => ReactElement
  showVisitWorkareaName?: boolean
  quantityTitleOverride?: string
}

type VisibleOverrideDialogOptions = 'none' | 'custom-name' | 'custom-cost' | 'custom-margin' | 'custom-price' | 'custom-total-price'

export type ItemTableColumn = 'name' | 'workarea' | 'visitDetails' | 'additional' | 'unsure' | 'isBillable' | 'isTaxable' | 'quantity'
  | 'cost' | 'totalCost' | 'costPlusTax' | 'breakeven' | 'totalBreakeven' | 'profit' | 'price' | 'totalPrice' | 'actions' | 'totalEstimatedQuantity'

type ItemTableColumnType = ColumnType<ViewItem> & { columnName: ItemTableColumn, renderSummary?: () => React.ReactNode }

const ItemTable = ({
  viewItems,
  workareaItemType,
  budgets,
  serverState,
  isLoading,
  visibleColumns,
  workareas,
  sortable = false,
  customSummary,
  showVisitWorkareaName = false,
  quantityTitleOverride
}: Props) => {
  const itemsContext = useContext(ItemsContext)
  const dispatch = itemsContext.dispatch

  const [overrideDialogItem, setOverrideDialogItem] = useState<ViewItem | undefined>(undefined)
  const [visibleOverrideDialog, setVisibleOverrideDialog] = useState<VisibleOverrideDialogOptions>('none')
  const { draggingViewItem } = useContext(CatalogItemDndContext)
  let viewItemsWithDraggingItem = viewItems
  if (draggingViewItem) {
    viewItemsWithDraggingItem = viewItems.every(x => x.isEmptyRow) ? [draggingViewItem] : [...viewItems, draggingViewItem]
  }

  const containerRef = useRef<any>()

  const openOverrideDialog = (dialog: VisibleOverrideDialogOptions, item: ViewItem) => {
    if (item.isEntireRowDisabled) {
      return
    }
    setOverrideDialogItem(item)
    setVisibleOverrideDialog(dialog)
  }

  const closeOverrideDialog = () => {
    setVisibleOverrideDialog('none')
    setOverrideDialogItem(undefined)
  }

  const customNameDialogSubmit = (value: string) => {
    dispatch({ type: 'NAME_CHANGED', guid: overrideDialogItem!.guid, value, serverState })
    closeOverrideDialog()
  }

  const customCostDialogSubmit = (value: number) => {
    dispatch({ type: 'CUSTOM_COST_CHANGED', guid: overrideDialogItem!.guid, value, serverState })
    closeOverrideDialog()
  }

  const customMarginDialogSubmit = (value: number) => {
    dispatch({ type: 'CUSTOM_MARGIN_CHANGED', guid: overrideDialogItem!.guid, value, serverState })
    closeOverrideDialog()
  }

  const customPriceDialogSubmit = (value: number) => {
    dispatch({ type: 'CUSTOM_PRICE_CHANGED', guid: overrideDialogItem!.guid, value, serverState })
    closeOverrideDialog()
  }

  const customTotalPriceDialogSubmit = (value: number) => {
    dispatch({ type: 'CUSTOM_TOTAL_PRICE_CHANGED', guid: overrideDialogItem!.guid, value, serverState })
    closeOverrideDialog()
  }

  const displayEmptyOrFormattedAmount = (value: number, item: ViewItem) => item.isEmptyRow ? <></> : centsToDollarsDisplay(value)

  const columns: ItemTableColumnType[] = [
    {
      columnName: 'name',
      title: 'Item',
      dataIndex: 'name',
      render: (_, item) => {
        if (item.isEmptyRow) {
          return <></>
        }
        return <Space>
          <InvoicedIcon item={item} />
          <ItemDiffersIcon item={item} />
          <CustomizableValue
            canCustomize={item.canCustomizeName}
            isCustomized={item.hasCustomName}
            item={item}
            openOverrideDialog={() => openOverrideDialog('custom-name', item)}
          >
            {item.name}
          </CustomizableValue>
          {item.shouldShowPricingSync &&
            <Tooltip title={`Sync item pricing with ${budgets[0].year} budget`}>
              <Link onClick={() => dispatch({ type: 'SYNC_ITEM_PRICING', guid: item.guid, budgets, serverState })}><FontAwesomeIcon icon={faRotate} /></Link>
            </Tooltip>
          }
          <Link href={`/#!/items#${item.catalogItemId}`} target="_blank" className="detail-icon"><FontAwesomeIcon icon={faMagnifyingGlassPlus} /></Link>
        </Space>
      },
      className: 'item-name'
    },
    {
      columnName: 'workarea',
      title: 'Workarea',
      dataIndex: 'workareaName',
      className: 'workarea-name'
    },
    {
      columnName: 'visitDetails',
      title: 'Item',
      key: 'visitDetails',
      render: (_, item) => <VisitDetail item={item} showVisitWorkareaName={showVisitWorkareaName} />
    },
    {
      columnName: 'additional',
      title: 'Additional',
      key: 'additional',
      render: (_, item) => <LockableSwitch
        item={item}
        checked={item.isAdditional}
        disabled={item.isAdditionalLocked}
        onChange={value => dispatch({ type: 'IS_ADDITIONAL_CHANGED', guid: item.guid, workareas, isVisitReviewed: item.isVisitReviewed, value, serverState })}
      />
    },
    {
      columnName: 'unsure',
      title: 'Unsure',
      key: 'unsure',
      render: (_, item) => <LockableSwitch
        item={item}
        checked={item.unsure}
        disabled={item.isBillableLocked}
        onChange={value => dispatch({ type: 'UNSURE_CHANGED', guid: item.guid, workareas, isVisitReviewed: item.isVisitReviewed, value, serverState })}
      />
    },
    {
      columnName: 'isBillable',
      title: 'Billable',
      dataIndex: 'isBillable',
      render: (isBillable, item) => <LockableSwitch
        item={item}
        checked={isBillable}
        disabled={item.isBillableLocked}
        onChange={value => dispatch({ type: 'BILLABLE_CHANGED', guid: item.guid, workareas, isVisitReviewed: item.isVisitReviewed, value, serverState })}
      />
    },
    {
      columnName: 'isTaxable',
      title: 'Taxable',
      dataIndex: 'chargeTaxOnInvoice',
      render: (isTaxable, item) => <LockableSwitch
        item={item}
        checked={isTaxable}
        onChange={value => dispatch({ type: 'TAXABLE_CHANGED', guid: item.guid, isVisitReviewed: item.isVisitReviewed, value, serverState })}
      />
    },
    {
      columnName: 'totalEstimatedQuantity',
      title: 'Estimated Quantity',
      dataIndex: 'totalEstimatedQuantity',
      render: (qty, item) => item.isEmptyRow ? <></> : <>{qty ?? 0}</>
    },
    {
      columnName: 'quantity',
      title: quantityTitleOverride ?? 'Quantity',
      dataIndex: 'quantity',
      render: (_, item) => {
        if (item.isEmptyRow) {
          return <></>
        }

        return <ItemQuantity
          item={item}
          onChange={value => dispatch({ type: 'QUANTITY_CHANGED', guid: item.guid, value, serverState })}
          showRateCalculator={workareaItemType === WorkareaItemTypes.Estimated}
        />
      },
    },
    {
      columnName: 'cost',
      title: 'Cost',
      dataIndex: 'cost',
      render: (_, item) => <CustomizableValue
        isCustomized={item.hasCustomCost}
        canCustomize={item.canCustomizeCost} item={item}
        openOverrideDialog={() => openOverrideDialog('custom-cost', item)}
      >
        {centsToDollarsDisplay(item.cost)}
      </CustomizableValue>,
      align: 'right'
    },
    {
      columnName: 'totalCost',
      title: 'Total',
      dataIndex: 'totalCost',
      render: displayEmptyOrFormattedAmount,
      align: 'right'
    },
    {
      columnName: 'costPlusTax',
      title: 'Cost + Tax',
      dataIndex: 'costPlusTax',
      render: (_, item) => <CustomizableValue
        isCustomized={item.hasCustomCost}
        canCustomize={item.canCustomizeCost} item={item}
        openOverrideDialog={() => openOverrideDialog('custom-cost', item)}
      >
        {centsToDollarsDisplay(item.costPlusTax)}
      </CustomizableValue>,
      align: 'right'
    },
    {
      columnName: 'breakeven',
      title: 'Breakeven',
      dataIndex: 'breakeven',
      render: displayEmptyOrFormattedAmount,
      align: 'right'
    },
    {
      columnName: 'totalBreakeven',
      title: 'Total Cost',
      dataIndex: 'totalBreakeven',
      render: displayEmptyOrFormattedAmount,
      align: 'right',
      renderSummary: () => centsToDollarsDisplay(viewItems.reduce((acc, x) => acc + (x.isDeleted ? 0 : x.totalBreakeven), 0))
    },
    {
      columnName: 'profit',
      title: 'Profit',
      dataIndex: 'profitMargin',
      render: (_, item) => <CustomizableValue
        canCustomize={item.canCustomizeProfitMargin}
        isCustomized={item.hasCustomProfitMargin}
        item={item}
        openOverrideDialog={() => openOverrideDialog('custom-margin', item)}
      >
        {percentageDisplay((item.profitMargin ?? item.profitMargin ?? 0) * 100)}
      </CustomizableValue>,
      align: 'right'
    },
    {
      columnName: 'price',
      title: 'Price',
      dataIndex: 'unitPrice',
      render: (_, item) => <CustomizableValue
        canCustomize={true}
        isCustomized={item.hasCustomUnitPrice}
        item={item}
        openOverrideDialog={() => openOverrideDialog('custom-price', item)}
      >
        {centsToDollarsDisplay(item.unitPrice)}
      </CustomizableValue>,
      align: 'right'
    },
    {
      columnName: 'totalPrice',
      title: 'Total Price',
      dataIndex: 'totalPrice',
      render: (_, item) => <CustomizableValue
        canCustomize={item.canCustomizeTotalPrice}
        isCustomized={item.hasCustomTotalPrice}
        item={item}
        openOverrideDialog={() => openOverrideDialog('custom-total-price', item)}
      >
        {item.ifBillableTotalPrice === undefined
          ?
          centsToDollarsDisplay(item.totalPrice)
          :
          <Text type='secondary' delete>
            <Tooltip title={'This item is not billable and therefore does not add to the total cost.'}>
              {centsToDollarsDisplay(item.ifBillableTotalPrice)}
            </Tooltip>
          </Text>
        }
      </CustomizableValue>
      ,
      align: 'right',
      renderSummary: () => centsToDollarsDisplay(viewItems.reduce((acc, x) => acc + (x.isDeleted ? 0 : x.totalPrice), 0))
    },
    {
      columnName: 'actions',
      key: 'actions',
      render: (_, item) => {
        if (item.isEmptyRow || item.isFromTimesheets || (item.isEntireRowDisabled && !item.isDeleted)) {
          return <></>
        }

        return <ItemDeleteLink
          isDeleted={item.isDeleted}
          onClick={() => dispatch({ type: item.isDeleted ? 'UNDELETE_ITEM' : 'DELETE_ITEM', guid: item.guid, serverState })}
        />
      },
      align: 'right'
    },
  ]

  const filteredColumns = columns.filter(x => visibleColumns.includes(x.columnName))

  return <div ref={containerRef} style={{ width: '100%', overflowX: 'auto' }}>
    <SortableContext items={viewItemsWithDraggingItem.map(x => x.dragId)} strategy={verticalListSortingStrategy}>
      <StyledTable
        loading={isLoading}
        dataSource={viewItemsWithDraggingItem}
        columns={filteredColumns}
        rowKey="dragId"
        pagination={false}
        size="small"
        components={{
          header: {
            row: sortable ? DraggableSortableHeader : UndraggableSortableHeader,
          },
          body: {
            row: sortable ? DraggableSortableRow : UndraggableSortableRow
          }
        }}
        rowClassName={(item: ViewItem) => item.isDeleted ? 'deleted' : ''}
        summary={() => {
          if (customSummary) { return customSummary(viewItems) }
          return <Table.Summary.Row style={{ fontWeight: 'bold' }}>
            {sortable && <Table.Summary.Cell index={0} />}
            {filteredColumns.map((c, i) => {
              return <Table.Summary.Cell index={i + 1} key={`summary-${c.columnName}`}>
                <Row justify="end">
                  {c.renderSummary && c.renderSummary()}
                </Row>
              </Table.Summary.Cell>
            })}
          </Table.Summary.Row>
        }}
      />
    </SortableContext>
    <OverrideStringValueModal
      visible={visibleOverrideDialog === 'custom-name'}
      currentValue={overrideDialogItem?.name}
      defaultValue={overrideDialogItem?.defaultName ?? ''}
      label='catalog name'
      name={'this item'}
      onCancel={() => closeOverrideDialog()}
      onSubmit={customNameDialogSubmit}
      onReset={() => customNameDialogSubmit(overrideDialogItem?.defaultName ?? '')}
      getContainer={() => containerRef.current}
      maxLength={150}
    />
    <OverrideNumericValueModal
      visible={visibleOverrideDialog === 'custom-cost'}
      currentValue={overrideDialogItem?.cost}
      defaultValue={overrideDialogItem?.defaultCost ?? 0}
      label='catalog cost'
      name={overrideDialogItem?.name ?? ''}
      type='cents'
      onCancel={() => closeOverrideDialog()}
      onSubmit={customCostDialogSubmit}
      onReset={() => customCostDialogSubmit(overrideDialogItem?.defaultCost ?? 0)}
      min={0}
      getContainer={() => containerRef.current}
    />
    <OverrideNumericValueModal
      visible={visibleOverrideDialog === 'custom-margin'}
      currentValue={overrideDialogItem?.profitMargin}
      defaultValue={overrideDialogItem?.defaultProfitMargin ?? 0}
      label='non-customized profit margin'
      name={overrideDialogItem?.name ?? ''}
      type='percentage'
      onCancel={() => closeOverrideDialog()}
      onSubmit={customMarginDialogSubmit}
      onReset={() => customMarginDialogSubmit(overrideDialogItem?.defaultProfitMargin ?? 0)}
      max={.9999}
      min={0}
      precision={2}
      getContainer={() => containerRef.current}
    />
    <OverrideNumericValueModal
      visible={visibleOverrideDialog === 'custom-price'}
      currentValue={overrideDialogItem?.unitPrice}
      defaultValue={overrideDialogItem?.defaultUnitPrice ?? 0}
      label='non-customized price'
      name={overrideDialogItem?.name ?? ''}
      type='cents'
      onCancel={() => closeOverrideDialog()}
      onSubmit={customPriceDialogSubmit}
      onReset={() => customPriceDialogSubmit(overrideDialogItem?.defaultUnitPrice ?? 0)}
      getContainer={() => containerRef.current}
    />
    <OverrideNumericValueModal
      visible={visibleOverrideDialog === 'custom-total-price'}
      currentValue={overrideDialogItem?.totalPrice}
      defaultValue={overrideDialogItem?.defaultTotalPrice ?? 0}
      label='non-customized total price'
      name={overrideDialogItem?.name ?? ''}
      type='cents'
      onCancel={() => closeOverrideDialog()}
      onSubmit={customTotalPriceDialogSubmit}
      onReset={() => customTotalPriceDialogSubmit(overrideDialogItem?.defaultTotalPrice ?? 0)}
      getContainer={() => containerRef.current}
    />
  </div>
}
export default ItemTable
